import { getPersistentDeviceId } from "@/lib/constants";
import { LOS_API_ENDPOINTS, RTK_TAGS } from "@/lib/constants/Api";
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithMaintenance } from "../baseQueryMaintainence";

const losApi = createApi({
  reducerPath: "los",
  tagTypes: [
    RTK_TAGS.APPLICATION_TAG,
    RTK_TAGS.CUSTOMER_TAG,
    RTK_TAGS.ADDRESS_TAG,
    RTK_TAGS.APP_STAGE_CONFIG_TAG,
    RTK_TAGS.CREDIT_SCORE,
  ],
  baseQuery: baseQueryWithMaintenance({
    baseUrl: `${process.env.NEXT_PUBLIC_LOS_API_URL}`,
    prepareHeaders: async (headers) => {
      const token = await sessionStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      const deviceId = getPersistentDeviceId();
      headers.set("x-device-type", `web`);
      headers.set("x-device-id", deviceId);
      headers.set("x-device-name", `${window?.navigator?.userAgent}`);
      return headers;
    },
  }),
  endpoints: (builder) => {
    return {
      // CUSTOMER ENDPOINTS
      getCustomer: builder.query({
        query: () => {
          return {
            url: `v1${LOS_API_ENDPOINTS.CUSTOMER_ENDPOINTS().CUSTOMER}`,
            method: "GET",
          };
        },
        providesTags: [RTK_TAGS.CUSTOMER_TAG],
        invalidatesTags: [RTK_TAGS.APPLICATION_TAG],
      }),
      addCustomerInfo: builder.mutation({
        query: (body) => {
          return {
            url: `v1${
              LOS_API_ENDPOINTS.CUSTOMER_ENDPOINTS(body?.appId)
                .CUSTOMER_PERSONAL_DETAILS
            }`,
            method: "POST",
            body,
          };
        },
        invalidatesTags: [RTK_TAGS.CUSTOMER_TAG, RTK_TAGS.APPLICATION_TAG],
      }),
      // APPLICATION ENDPOINTS
      vpaList: builder.query({
        query: () => {
          return {
            url: `v1${LOS_API_ENDPOINTS.VPALIST}`,
            method: "GET",
          };
        },
      }),
      getApplication: builder.query({
        query: () => {
          return {
            url: `v1${LOS_API_ENDPOINTS.APPLICATION_ENDPOINTS().APPLICATION}`,
            method: "GET",
          };
        },
        providesTags: [RTK_TAGS.APPLICATION_TAG],
      }),
      createApplication: builder.mutation({
        query: (body) => {
          return {
            url: `v1${LOS_API_ENDPOINTS.APPLICATION_ENDPOINTS().APPLICATION}`,
            method: "POST",
            body,
          };
        },
        invalidatesTags: [RTK_TAGS.APPLICATION_TAG],
      }),
      patchApplication: builder.mutation({
        query: (body) => {
          return {
            url: `v1${
              LOS_API_ENDPOINTS.APPLICATION_ENDPOINTS(body?.appId)?.APPLICATION
            }`,
            method: "PATCH",
            body,
          };
        },
        invalidatesTags: [RTK_TAGS.APPLICATION_TAG],
      }),
      getApplicationStageConfig: builder.query({
        query: ({ appId, stage }) => {
          return {
            url: `v1${
              LOS_API_ENDPOINTS.APPLICATION_ENDPOINTS(appId, stage)
                .APP_STAGE_CONFIG
            }`,
            method: "GET",
          };
        },
        providesTags: [RTK_TAGS.APP_STAGE_CONFIG_TAG],
      }),
      applyLoan: builder.mutation({
        query: (body) => {
          return {
            url: `v1${
              LOS_API_ENDPOINTS.APPLICATION_ENDPOINTS(body?.appId)?.LOAN_AMOUNT
            }`,
            method: "POST",
            body,
          };
        },
        invalidatesTags: [RTK_TAGS.APPLICATION_TAG, RTK_TAGS.CUSTOMER_TAG],
      }),
      getApplicationConfig: builder.query({
        query: () => {
          return {
            url: `v1${LOS_API_ENDPOINTS.MASTER_API_ENDPOINTS.APPLICATION_CONFIG}`,
            method: "GET",
          };
        },
      }),
      setMarketingData: builder.mutation({
        query: (body) => {
          return {
            url: "v1/marketing-id",
            method: "POST",
            body,
          };
        },
        invalidatesTags: [RTK_TAGS.CUSTOMER_TAG],
      }),
      // PAN ENDPOINTS
      verifyPan: builder.mutation({
        query: (body) => {
          return {
            url: `v1${LOS_API_ENDPOINTS.PAN(body?.appId)}`,
            method: "POST",
            body,
          };
        },
        invalidatesTags: [RTK_TAGS.CUSTOMER_TAG, RTK_TAGS.APPLICATION_TAG],
      }),
      flushPan: builder.mutation({
        query: (body) => {
          return {
            url: `v1${LOS_API_ENDPOINTS.PAN(body?.appId)}/flush`,
            method: "POST",
            body,
          };
        },
        invalidatesTags: [RTK_TAGS.CUSTOMER_TAG, RTK_TAGS.APPLICATION_TAG],
      }),
      acceptPan: builder.mutation({
        query: (body) => {
          return {
            url: `v1${LOS_API_ENDPOINTS.PAN(body?.appId)}/accept`,
            method: "POST",
            body,
          };
        },
        invalidatesTags: [RTK_TAGS.CUSTOMER_TAG, RTK_TAGS.APPLICATION_TAG],
      }),
      initiateCkyc: builder.mutation({
        query: (body) => {
          return {
            url: `v2${LOS_API_ENDPOINTS.CKYC_ENDPOINTS(body?.appId).SEND_OTP}`,
            method: "POST",
            body,
          };
        },
        invalidatesTags: [RTK_TAGS.APPLICATION_TAG, RTK_TAGS.CUSTOMER_TAG],
      }),
      validateCkyc: builder.mutation({
        query: (body) => {
          return {
            url: `v2${
              LOS_API_ENDPOINTS.CKYC_ENDPOINTS(body?.appId).VALIDATE_OTP
            }`,
            method: "POST",
            body,
          };
        },
        invalidatesTags: [RTK_TAGS.APPLICATION_TAG, RTK_TAGS.CUSTOMER_TAG],
      }),
      skipCkyc: builder.mutation({
        query: (body) => {
          return {
            url: `v2${LOS_API_ENDPOINTS.CKYC_ENDPOINTS(body?.appId).SKIP_CKYC}`,
            method: "POST",
            body,
          };
        },
        invalidatesTags: [RTK_TAGS.APPLICATION_TAG, RTK_TAGS.CUSTOMER_TAG],
      }),
      getDigiKycRedirectUrl: builder.query({
        query: ({ appId }) => {
          return {
            url: `v2${
              LOS_API_ENDPOINTS.CKYC_ENDPOINTS(appId).DIGI_KYC_REDIRECT_URL
            }`,
          };
        },
      }),

      confirmAddress: builder.mutation({
        query: (body) => {
          return {
            url: `v2${
              LOS_API_ENDPOINTS.CKYC_ENDPOINTS(body?.appId)?.SELECT_ADDRESS
            }`,
            method: "PATCH",
            body,
          };
        },
        invalidatesTags: [RTK_TAGS.APPLICATION_TAG, RTK_TAGS.ADDRESS_TAG],
      }),
      consentCreate: builder.mutation({
        query: (body) => {
          return {
            url: `v1${LOS_API_ENDPOINTS.CONSENT}`,
            method: "POST",
            body,
          };
        },
      }),
      // ADDRESS ENDPOINTS
      getAddress: builder.query({
        query: (appId) => {
          return {
            url: `v1${
              LOS_API_ENDPOINTS.ADDRESS_ENDPOINTS(appId).GET_ADDRESS
            }?appId=${appId}`,
            method: "GET",
          };
        },
        providesTags: [RTK_TAGS.ADDRESS_TAG],
      }),
      addAddress: builder.mutation({
        query: (body) => {
          return {
            url: `v1${
              LOS_API_ENDPOINTS?.ADDRESS_ENDPOINTS(body?.appId)?.MUTATE_ADDRESS
            }`,
            method: "POST",
            body,
          };
        },
        invalidatesTags: [
          RTK_TAGS.ADDRESS_TAG,
          RTK_TAGS.APPLICATION_TAG,
          RTK_TAGS.CUSTOMER_TAG,
        ],
      }),
      patchAddress: builder.mutation({
        query: (body) => {
          return {
            url: `v1${
              LOS_API_ENDPOINTS?.ADDRESS_ENDPOINTS(body?.appId)?.MUTATE_ADDRESS
            }`,
            method: "PATCH",
            body,
          };
        },
        invalidatesTags: [
          RTK_TAGS.ADDRESS_TAG,
          RTK_TAGS.APPLICATION_TAG,
          RTK_TAGS.CUSTOMER_TAG,
        ],
      }),
      patchRplAddress: builder.mutation({
        query: (body) => {
          return {
            url: `v1${
              LOS_API_ENDPOINTS?.ADDRESS_ENDPOINTS(body?.appId)
                ?.UPDATE_ADDRESS_RL
            }`,
            method: "PATCH",
            body,
          };
        },
        invalidatesTags: [RTK_TAGS.ADDRESS_TAG, RTK_TAGS.APPLICATION_TAG],
      }),
      confirmRplAddress: builder.mutation({
        query: (body) => {
          return {
            url: `v1${LOS_API_ENDPOINTS.ADDRESS_ENDPOINTS(body?.appId).CONFIRM_ADDRESS_RL}`,
            method: "POST",
          };
        },
        invalidatesTags: [RTK_TAGS.ADDRESS_TAG, RTK_TAGS.APPLICATION_TAG],
      }),
      // DASHBOARD ENDPOINTS
      getMyLoans: builder.query({
        query: () => {
          return {
            url: "v1/app/myloans",
            method: "GET",
          };
        },
      }),
      getLoanDetails: builder.query({
        query: (appId) => {
          return {
            url: `v1/app/${appId}/loandetails`,
            method: "GET",
          };
        },
      }),
      getLoanStatus: builder.query({
        query: () => {
          return {
            url: `v1/app/loanstatus`,
            method: "GET",
          };
        },
      }),
      getAvailableDocuments: builder.query({
        query: ({ appId }) => {
          return {
            url: `v1/app/${appId}/documents`,
            method: "GET",
          };
        },
      }),
      emailDocument: builder.mutation({
        query: (body) => {
          return {
            url: `v1/app/${body?.appId}/emaildocument`,
            method: "POST",
            body,
          };
        },
      }),
      generateSoaReportRequest: builder.mutation({
        query: (body) => {
          return {
            url: `v1/app/${body?.appId}/soa-report`,
            method: "POST",
            body,
          };
        },
      }),
      getSoaReport: builder.query({
        query: ({ appId, requestId }) => {
          return {
            url: `v1/app/${appId}/${requestId}/soa-report`,
            method: "GET",
          };
        },
      }),
      emailSoareport: builder.mutation({
        query: (body) => {
          return {
            url: `v1/app/${body?.appId}/${body?.requestId}/email-soa`,
            method: "POST",
            body,
          };
        },
      }),
      getLoanAgreement: builder.query({
        query: ({ appId }) => {
          return {
            url: `v1/app/${appId}/loan-agreement`,
            method: "GET",
          };
        },
      }),
      getEligibleLoans: builder.query({
        query: () => {
          return {
            url: `v1/app/dashboard/pre-loans`,
            method: "GET",
          };
        },
      }),
      postOptForLoanOffer: builder.mutation({
        query: ({ type }) => {
          return {
            url: `v1/app/dashboard/${type}/accept`,
            method: "GET",
          };
        },
      }),

      // MASTER DATA ENDPOINTS
      getPurposeOfLoanList: builder.query({
        query: () => {
          return {
            url: "v1/purposeofloan",
            method: "GET",
          };
        },
      }),
      getMaritalStatusList: builder.query({
        query: () => {
          return {
            url: "v1/maritalstatus",
            method: "GET",
          };
        },
      }),
      getLanguagesList: builder.query({
        query: () => {
          return {
            url: "v1/language",
            method: "GET",
          };
        },
      }),
      getIndustryTypeList: builder.query({
        query: () => {
          return {
            url: "v1/industrytype-details",
            method: "GET",
          };
        },
      }),
      getEmployementTypeList: builder.query({
        query: () => {
          return {
            url: "v1/employeetype-details",
            method: "GET",
          };
        },
      }),
      getEducationList: builder.query({
        query: () => {
          return {
            url: "v1/education",
            method: "GET",
          };
        },
      }),
      getCompanyNameList: builder.mutation({
        query: (queryName) => {
          return {
            url: `v1/companyname-details`,
            method: "POST",
            body: { companyNamePrefix: queryName },
          };
        },
      }),
      getProductsList: builder.query({
        query: () => {
          return {
            url: "v1/explore-products",
            method: "GET",
          };
        },
      }),
      getLoaderQuotesList: builder.query({
        query: () => {
          return {
            url: "v1/stage-pooling",
            method: "GET",
          };
        },
      }),
      getGendersList: builder.query({
        query: () => {
          return {
            url: "v1/gender",
            method: "GET",
          };
        },
      }),
      // WORK DETAILS ENDPOINTS
      postWorkDetails: builder.mutation({
        query: (body) => {
          return {
            url: `v1${LOS_API_ENDPOINTS.WORK_DETAILS(body?.appId)}`,
            method: "POST",
            body,
          };
        },
        invalidatesTags: [RTK_TAGS.APPLICATION_TAG, RTK_TAGS.CUSTOMER_TAG],
      }),
      verifyEpfoMail: builder.mutation({
        query: (body) => {
          return {
            url: `v1${
              LOS_API_ENDPOINTS.OFFICALEMAIL_ENDPOINTS(body?.appId)
                .VERIFY_EPFO_MAIL
            }`,
            method: "POST",
            body,
          };
        },
      }),
      // EMAIL OTP ENDPOINTS (used for both PERSONAL and WORK email verification)
      sendEmailOtp: builder.mutation({
        query: (body) => {
          return {
            url: `v1${
              LOS_API_ENDPOINTS.EMAIL_OTP_ENDPOINTS(body?.appId).SEND_EMAIL_OTP
            }`,
            method: "POST",
            body,
          };
        },
      }),
      verifyEmailOtp: builder.mutation({
        query: (body) => {
          return {
            url: `v1${
              LOS_API_ENDPOINTS.EMAIL_OTP_ENDPOINTS(body?.appId)
                .VERIFY_EMAIL_OTP
            }`,
            method: "POST",
            body,
          };
        },
      }),
      getMandateModes: builder.query({
        query: ({ appId }) => {
          return {
            url: `v1${
              LOS_API_ENDPOINTS.MANDATE_ENDPOINTS(appId).MANDATE_MODES
            }`,
            method: "GET",
          };
        },
      }),
      checkServicedPincode: builder.mutation({
        query: (body) => {
          return {
            url: "v1/serviceability",
            method: "POST",
            body,
          };
        },
      }),
      getRepaymentSetupApi: builder.query({
        query: (body) => {
          return {
            url: `v1${
              LOS_API_ENDPOINTS.MANDATE_ENDPOINTS(body?.appId).REPAYMENT_SETUP
            }`,
            method: "GET",
          };
        },
      }),
      // LEGALITY ENDPOINTS
      getESign: builder.query({
        query: (body) => {
          return {
            url: `v1${
              LOS_API_ENDPOINTS.LEEGALITY_ENDPOINTS(body?.appId).ESIGN
            }`,
            method: "GET",
          };
        },
      }),
      getRelations: builder.query({
        query: () => {
          return {
            url: `v1${LOS_API_ENDPOINTS.LEEGALITY_ENDPOINTS().RELATIONS}`,
            method: "GET",
          };
        },
      }),
      createNominee: builder.mutation({
        query: (body) => {
          return {
            url: `v1${
              LOS_API_ENDPOINTS.LEEGALITY_ENDPOINTS(body?.appId).NOMINEE_DETAILS
            }`,
            method: "POST",
            body,
          };
        },
        invalidatesTags: [RTK_TAGS.CUSTOMER_TAG, RTK_TAGS.APPLICATION_TAG],
      }),
      digikycConsent: builder.mutation({
        query: (body) => {
          return {
            url: `v1${LOS_API_ENDPOINTS.DIGIKYC_CONSENT(body?.appId)}`,
            method: "POST",
            body,
          };
        },
      }),
      kycTokenStatus: builder.query({
        query: () => {
          return {
            url: `v1${LOS_API_ENDPOINTS.KYC_TOKEN_ENDPOINT()}`,
            method: "GET",
          };
        },
      }),

      getCheckELigibilityStatus: builder.query({
        query: () => {
          return {
            url: `v1${
              LOS_API_ENDPOINTS.CHECK_ELIGIBILITY_ENDPOINT()
                .CHECK_ELIGIBILITY_STATUS
            }`,
            method: "GET",
          };
        },
      }),
      checkEligibilityPanDetails: builder.mutation({
        query: (body) => {
          return {
            url: `v1${
              LOS_API_ENDPOINTS.CHECK_ELIGIBILITY_ENDPOINT().PAN_DETAILS
            }`,
            method: "POST",
            body,
          };
        },
      }),
      checkEligibilityOffer: builder.mutation({
        query: (body) => {
          return {
            url: `v1${
              LOS_API_ENDPOINTS.CHECK_ELIGIBILITY_ENDPOINT()
                .CHECK_ELIGIBILTY_OFFER
            }`,
            method: "POST",
            body,
          };
        },
      }),
      // mappls
      getReverseGeocodeMappls: builder.query({
        query: (params) => {
          return {
            url: `v1${LOS_API_ENDPOINTS.MAPPLS_ENDPOINT().REVERSE_GEOCODE}`,
            method: "GET",
            params,
          };
        },
      }),
      mandateCalculation: builder.query({
        query: () => {
          return {
            url: `v1/currentAndNextMonths/3`,
            method: "GET",
          };
        },
      }),

      //ekyc
      ekycConsent: builder.mutation({
        query: ({ appId, payload }) => {
          return {
            url: LOS_API_ENDPOINTS.EKYC_ENDPOINTS(appId)?.EKYC_CONSENT,
            method: "POST",
            body: payload,
          };
        },
        // invalidatesTags: [RTK_TAGS.APPLICATION_TAG],
      }),
    };
  },
});

export const {
  useAcceptPanMutation,
  useFlushPanMutation,
  useApplyLoanMutation,
  useGetApplicationQuery,
  usePatchApplicationMutation,
  useCreateApplicationMutation,
  useGetAddressQuery,
  useAddAddressMutation,
  usePatchAddressMutation,
  useGetApplicationConfigQuery,
  useGetCustomerQuery,
  useAddCustomerInfoMutation,
  useGetMyLoansQuery,
  useGetLoanDetailsQuery,
  useGetLoanStatusQuery,
  useGetAvailableDocumentsQuery,
  useEmailDocumentMutation,
  useGenerateSoaReportRequestMutation,
  useGetSoaReportQuery,
  useEmailSoareportMutation,
  useGetLoanAgreementQuery,
  useGetRelationsQuery,
  useGetESignQuery,
  useCreateNomineeMutation,
  useGetRepaymentSetupApiQuery,
  useGetPurposeOfLoanListQuery,
  useGetMaritalStatusListQuery,
  useGetLanguagesListQuery,
  useGetIndustryTypeListQuery,
  useGetEmployementTypeListQuery,
  useGetEducationListQuery,
  useGetCompanyNameListMutation,
  useGetProductsListQuery,
  useGetLoaderQuotesListQuery,
  useGetGendersListQuery,
  useGetMandateModesQuery,
  useCheckServicedPincodeMutation,
  useSetMarketingDataMutation,
  useVerifyPanMutation,
  useInitiateCkycMutation,
  useConsentCreateMutation,
  usePostWorkDetailsMutation,
  useDigikycConsentMutation,
  useVpaListQuery,
  useKycTokenStatusQuery,
  useSkipCkycMutation,
  useValidateCkycMutation,
  useLazyGetDigiKycRedirectUrlQuery,
  useConfirmAddressMutation,
  useGetEligibleLoansQuery,
  usePostOptForLoanOfferMutation,
  usePatchRplAddressMutation,
  useConfirmRplAddressMutation,
  useLazyGetReverseGeocodeMapplsQuery,
  useGetApplicationStageConfigQuery,
  useVerifyEpfoMailMutation,
  useSendEmailOtpMutation,
  useVerifyEmailOtpMutation,
  useEkycConsentMutation,
} = losApi;
export { losApi };
